import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Briefcase, Lightbulb, Stethoscope, Building2, Church, Megaphone, Eye, MousePointerClick, Star, ChevronRight, Pencil, Trash2, LogOut, AlertCircle, Package, Crown, Zap, Calendar, Radio, ImagePlus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/auth/AuthContext";
import StoryUploadModal from "@/components/StoryUploadModal";

const KIND_META = {
  business: { Icon: Briefcase, color: "bg-orange text-white", labelKey: "kindBusiness" },
  venture: { Icon: Lightbulb, color: "bg-amber-500 text-white", labelKey: "kindVenture" },
  professional: { Icon: Stethoscope, color: "bg-teal text-white", labelKey: "kindProfessional" },
  ngo: { Icon: Building2, color: "bg-teal-deep text-white", labelKey: "kindNgo" },
  church: { Icon: Church, color: "bg-violet-600 text-white", labelKey: "kindChurch" },
  community_leader: { Icon: Megaphone, color: "bg-rose-500 text-white", labelKey: "kindCommunityLeader" },
};

const MyProfiles = () => {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [plan, setPlan] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/profiles/me");
      setItems(data.items || []);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setLoading(false); }
    try {
      const { data } = await api.get("/me/plan");
      setPlan(data);
    } catch (err) { /* ignore */ console.error("plan failed", err); }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const remove = async (p) => {
    if (!window.confirm(`${t.myProfiles.confirmDelete} "${p.name}"?`)) return;
    try { await api.delete(`/profiles/${p.id}`); await load(); }
    catch (err) { setError(formatApiError(err)); }
  };

  const totalStats = useMemo(() => {
    return items.reduce((acc, p) => ({
      views: acc.views + (p.view_count || 0),
      clicks: acc.clicks + (p.contact_click_count || 0),
      ratings: acc.ratings + (p.ratings_count || 0),
    }), { views: 0, clicks: 0, ratings: 0 });
  }, [items]);

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  const profilesMax = plan && plan.limits.profiles < 99 ? plan.limits.profiles : null;
  const profilesUsed = plan?.usage?.profiles ?? items.length;
  const atProfileLimit = profilesMax != null && profilesUsed >= profilesMax;
  const profilesRemaining = profilesMax != null ? Math.max(0, profilesMax - profilesUsed) : null;

  if (user === null) return <div className="container-tight py-24 text-center text-teal-soft">…</div>;
  if (user === false) return null;

  return (
    <section className="section bg-cream min-h-[80vh]">
      <div className="container-tight max-w-5xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="eyebrow text-orange">{t.myProfiles.eyebrow}</p>
            <h1 className="font-display text-4xl md:text-5xl text-teal-deep leading-tight mt-2">
              {t.myProfiles.title.replace("{name}", user?.full_name?.split(" ")[0] || user?.email?.split("@")[0] || "")}
            </h1>
            <p className="text-teal-soft text-base mt-2 max-w-2xl">{t.myProfiles.subtitle}</p>
          </div>
          <button onClick={() => { logout(); navigate("/"); }} className="text-sm text-teal-soft hover:text-orange inline-flex items-center gap-1" data-testid="logout-btn">
            <LogOut size={14} /> {t.auth.logoutLink || "Cerrar sesión"}
          </button>
        </div>

        {/* Plan card */}
        {plan && (
          <div className="mt-8 bg-white rounded-2xl border border-gray-200 p-5 flex items-center gap-4 flex-wrap" data-testid="plan-card">
            <span className={`w-12 h-12 rounded-xl flex items-center justify-center text-white ${plan.plan === "free" ? "bg-teal-soft" : plan.plan === "standard" ? "bg-orange" : plan.plan === "business" ? "bg-teal-deep" : "bg-amber-500"}`}>
              {plan.plan === "free" ? <Zap size={20} /> : <Crown size={20} />}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-widest font-bold text-orange">{t.myProfiles.yourPlan}</p>
              <p className="font-display text-2xl text-teal-deep leading-tight">{plan.label}</p>
              <p className="text-sm text-teal-soft mt-1">
                {plan.usage?.profiles ?? 0} / {plan.limits.profiles >= 99 ? (plan.limits.profiles === 999 ? "∞" : "+10") : plan.limits.profiles} {t.myProfiles.profilesLabel} · {plan.limits.products_per_profile === 999 ? "∞" : plan.limits.products_per_profile} {t.myProfiles.productsPerProfile}
              </p>
              {plan.limits.profiles < 99 && (
                <div className="mt-2 max-w-xs" data-testid="profiles-usage">
                  <p className="text-xs font-semibold text-teal-deep">
                    {t.myProfiles.profilesUsage
                      .replace("{used}", plan.usage?.profiles ?? 0)
                      .replace("{max}", plan.limits.profiles)}
                  </p>
                  <div className="mt-1 h-2 rounded-full bg-cream overflow-hidden border border-black/5">
                    <div
                      className={`h-full rounded-full transition-all ${(plan.usage?.profiles ?? 0) >= plan.limits.profiles ? "bg-red-400" : "bg-orange"}`}
                      style={{ width: `${Math.min(100, Math.round(((plan.usage?.profiles ?? 0) / plan.limits.profiles) * 100))}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
            <Link to="/plans" className="btn-teal-outline" data-testid="plans-link">{t.myProfiles.viewPlans}</Link>
          </div>
        )}

        {/* Aggregate stats */}
        {items.length > 0 && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="aggregate-stats">
            <StatCard Icon={Briefcase} value={items.length} label={t.myProfiles.totalProfiles} color="bg-orange" />
            <StatCard Icon={Eye} value={totalStats.views} label={t.myProfiles.totalViews} color="bg-teal" />
            <StatCard Icon={MousePointerClick} value={totalStats.clicks} label={t.myProfiles.totalClicks} color="bg-teal-deep" />
            <StatCard Icon={Star} value={totalStats.ratings} label={t.myProfiles.totalRatings} color="bg-amber-500" />
          </div>
        )}

        {/* Profiles */}
        <div className="mt-10 flex items-baseline justify-between gap-4 flex-wrap">
          <h2 className="font-display text-2xl text-teal-deep">{t.myProfiles.profilesHeading}</h2>
          {atProfileLimit ? (
            <Link to="/plans" className="inline-flex items-center gap-1.5 text-sm font-semibold text-orange bg-orange/10 border border-orange/30 rounded-full px-4 py-2" data-testid="profiles-limit-note">
              <Crown size={14} /> {t.myProfiles.profilesFull}
            </Link>
          ) : (
            <Link to={items.length === 0 ? "/profiles/new?welcome=1" : "/profiles/new"} className="btn-orange" data-testid="add-profile-btn">
              <Plus size={14} /> {t.myProfiles.addProfile}
              {profilesRemaining != null && (
                <span className="ml-1.5 text-xs font-bold bg-white/25 rounded-full px-2 py-0.5" data-testid="profiles-remaining">
                  {t.myProfiles.profilesRemaining.replace("{n}", profilesRemaining)}
                </span>
              )}
            </Link>
          )}
        </div>

        {error && (
          <p className="mt-4 text-red-600 text-sm inline-flex items-center gap-2" data-testid="my-profiles-error">
            <AlertCircle size={14} /> {error}
          </p>
        )}

        {loading ? (
          <div className="mt-6 space-y-3">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={`sk-${i}`} className="bg-white rounded-2xl h-32 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="mt-6 bg-white rounded-2xl border border-dashed border-gray-300 p-10 text-center" data-testid="my-profiles-empty">
            <Lightbulb className="text-orange mx-auto mb-3" size={40} />
            <h3 className="font-display text-2xl text-teal-deep">{t.myProfiles.emptyTitle}</h3>
            <p className="text-teal-soft mt-2 max-w-md mx-auto">{t.myProfiles.emptyBody}</p>
            <Link to="/profiles/new?welcome=1" className="btn-orange mt-6 inline-flex" data-testid="empty-add-profile-btn">
              <Plus size={14} /> {t.myProfiles.addProfile}
            </Link>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            {items.map((p) => <ProfileCard key={p.id} profile={p} onDelete={remove} t={t} />)}
          </div>
        )}
      </div>
    </section>
  );
};

const StatCard = ({ Icon, value, label, color }) => (
  <div className="bg-white rounded-2xl border border-gray-200 p-4 flex items-center gap-3">
    <span className={`w-10 h-10 rounded-full ${color} flex items-center justify-center shrink-0`}>
      <Icon size={16} />
    </span>
    <div className="min-w-0">
      <div className="font-display text-2xl text-teal-deep leading-none">{value}</div>
      <div className="text-xs uppercase tracking-wider text-teal-soft mt-1">{label}</div>
    </div>
  </div>
);

const ProfileCard = ({ profile, onDelete, t }) => {
  const meta = KIND_META[profile.kind] || KIND_META.business;
  const KindIcon = meta.Icon;
  const [storyOpen, setStoryOpen] = useState(false);
  return (
    <article className="bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col" data-testid={`profile-card-${profile.id}`}>
      <div className="p-5 flex-1">
        <div className="flex items-start gap-3">
          <span className={`w-12 h-12 rounded-full ${meta.color} flex items-center justify-center shrink-0 overflow-hidden`}>
            {profile.logo_url ? (
              <img src={profile.logo_url} alt={profile.name} className="w-full h-full object-cover" />
            ) : <KindIcon size={18} />}
          </span>
          <div className="flex-1 min-w-0">
            <span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${meta.color}`}>
              {t.myProfiles[meta.labelKey]}
            </span>
            <h3 className="font-display text-xl text-teal-deep leading-tight mt-1.5 truncate">{profile.name}</h3>
            {profile.city && (
              <p className="text-xs text-teal-soft mt-0.5 truncate">
                {[profile.city, profile.state].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
        </div>

        {profile.description && (
          <p className="text-sm text-teal-soft mt-3 leading-relaxed line-clamp-2">{profile.description}</p>
        )}

        {(!profile.logo_url || !profile.cover_url) && (
          <Link
            to={`/profiles/edit/${profile.id}?focus=media`}
            className="mt-4 flex items-center gap-3 rounded-2xl bg-orange/10 border-2 border-dashed border-orange/40 hover:bg-orange/15 hover:border-orange transition-colors px-3 py-3 group"
            data-testid={`add-media-cta-${profile.id}`}
          >
            <span className="w-10 h-10 rounded-full bg-orange text-white flex items-center justify-center shrink-0">
              <ImagePlus size={16} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-teal-deep leading-tight">
                {!profile.logo_url && !profile.cover_url
                  ? "Sube tu logo y portada"
                  : !profile.logo_url
                    ? "Falta el logo"
                    : "Falta la portada"}
              </p>
              <p className="text-xs text-teal-soft leading-snug">
                Los perfiles con foto reciben más contactos y aparecen primero en el directorio.
              </p>
            </div>
            <ChevronRight size={16} className="text-orange group-hover:translate-x-0.5 transition-transform" />
          </Link>
        )}

        <div className="mt-4 flex items-center gap-3 flex-wrap text-xs text-teal-soft">
          <span className="inline-flex items-center gap-1" title="Views"><Eye size={12} /> {profile.view_count}</span>
          <span className="inline-flex items-center gap-1" title="Clicks"><MousePointerClick size={12} /> {profile.contact_click_count}</span>
          {profile.ratings_count > 0 && (
            <span className="inline-flex items-center gap-1" title="Rating">
              <Star size={12} className="fill-orange text-orange" /> {profile.avg_rating?.toFixed(1)} ({profile.ratings_count})
            </span>
          )}
          {profile.volunteer && (
            <span className="inline-flex items-center gap-1 text-orange font-bold uppercase tracking-wider">
              · Vol.
            </span>
          )}
        </div>
      </div>
      <div className="px-5 py-3 bg-cream border-t border-gray-100 flex items-center gap-2 flex-wrap">
        <Link to={`/profiles/edit/${profile.id}`} className="text-sm text-teal hover:text-orange inline-flex items-center gap-1" data-testid={`edit-${profile.id}`}>
          <Pencil size={13} /> {t.myProfiles.editBtn}
        </Link>
        <Link to={`/profiles/${profile.id}/catalog`} className="text-sm text-orange hover:text-teal-deep inline-flex items-center gap-1 font-semibold" data-testid={`catalog-${profile.id}`}>
          <Package size={13} /> {t.myProfiles.catalogBtn}
        </Link>
        <button
          type="button"
          onClick={() => setStoryOpen(true)}
          className="text-sm text-orange hover:text-teal-deep inline-flex items-center gap-1 font-semibold"
          data-testid={`story-${profile.id}`}
        >
          <Radio size={13} /> Publicar historia
        </button>
        {profile.kind === "professional" && (
          <Link to={`/profiles/${profile.id}/agenda`} className="text-sm text-teal hover:text-teal-deep inline-flex items-center gap-1 font-semibold" data-testid={`agenda-${profile.id}`}>
            <Calendar size={13} /> {t.myProfiles.agendaBtn}
          </Link>
        )}
        {profile.legacy_entrepreneur_id ? (
          <Link to={`/entrepreneur/${profile.legacy_entrepreneur_id}`} className="text-sm text-teal-soft hover:text-teal inline-flex items-center gap-1 ml-auto" data-testid={`view-${profile.id}`}>
            {t.myProfiles.viewBtn} <ChevronRight size={12} />
          </Link>
        ) : (
          <span className="ml-auto" />
        )}
        <button type="button" onClick={() => onDelete(profile)} className="text-sm text-red-500 hover:text-red-700 inline-flex items-center gap-1" data-testid={`delete-${profile.id}`}>
          <Trash2 size={13} />
        </button>
      </div>
      {storyOpen && (
        <StoryUploadModal
          profileId={profile.id}
          profileName={profile.name}
          onClose={() => setStoryOpen(false)}
          onPublished={() => setStoryOpen(false)}
        />
      )}
    </article>
  );
};

export default MyProfiles;
